import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { SearchBar } from '@/components/ui/search-bar';
import { UserListRow } from '@/components/UserListRow';
import { AddAttendanceContent } from '@/content/AddAttendanceContent';
import type { Event, UserProfile } from '@/lib/types/database';
import type { EventAttendanceWithProfile } from '@/lib/types/attendance.types';
import type { EventRsvpWithProfile } from '@/lib/types/rsvps.types';
import { useSyncEventAttendance } from '@/lib/hooks/useAttendance';
import { getInitials } from '@/lib/utils/name.utils';
import { useActionBar } from '@/lib/context/ActionBarContext';

type AddEventAttendeesProps = {
  event: Event;
  attendance: EventAttendanceWithProfile[];
  profiles: UserProfile[];
  rsvps: EventRsvpWithProfile[];
};

export function AddEventAttendees({
  event,
  attendance,
  profiles,
  rsvps,
}: AddEventAttendeesProps) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const initialSelection = useMemo(
    () => attendance.map((record) => record.user_id),
    [attendance]
  );
  const [selectedUserIds, setSelectedUserIds] =
    useState<string[]>(initialSelection);
  const selectedUserIdsRef = useRef(selectedUserIds);
  const syncAttendance = useSyncEventAttendance();

  const profileMap = useMemo(() => {
    return new Map(profiles.map((profile) => [profile.id, profile]));
  }, [profiles]);

  const filteredProfiles = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return [];
    }

    return profiles.filter((profile) => {
      const candidate =
        profile.full_name || profile.email || profile.id || 'member';
      return candidate.toLowerCase().includes(query);
    });
  }, [profiles, searchTerm]);

  // Convert selected user IDs to profiles
  const selectedProfiles = useMemo(() => {
    return selectedUserIds
      .map((userId) => profileMap.get(userId))
      .filter((profile): profile is UserProfile => Boolean(profile));
  }, [selectedUserIds, profileMap]);

  // Convert RSVPs to profiles for the suggested section
  const suggestedProfiles = useMemo(() => {
    return rsvps
      .map((rsvp) => {
        const profile = profileMap.get(rsvp.user_id);
        return profile;
      })
      .filter((profile): profile is UserProfile => Boolean(profile));
  }, [rsvps, profileMap]);

  // When searching, show filtered results. When not searching, show suggested RSVPd users
  const visibleProfiles = searchTerm ? filteredProfiles : suggestedProfiles;

  // Check if there are any changes compared to initial selection
  const hasChanges = useMemo(() => {
    const currentSet = new Set(selectedUserIds);
    const initialSet = new Set(initialSelection);

    // If sizes differ, there are changes
    if (currentSet.size !== initialSet.size) {
      return true;
    }

    // Check if all current IDs are in initial set
    for (const id of currentSet) {
      if (!initialSet.has(id)) {
        return true;
      }
    }

    return false;
  }, [selectedUserIds, initialSelection]);

  const handleToggleUser = (userId: string) => {
    const nextSelection = selectedUserIds.includes(userId)
      ? selectedUserIds.filter((id) => id !== userId)
      : [...selectedUserIds, userId];

    setSelectedUserIds(nextSelection);
  };

  useEffect(() => {
    selectedUserIdsRef.current = selectedUserIds;
  }, [selectedUserIds]);

  const handleNavigateBack = useCallback(() => {
    navigate({
      to: '/events/$eventId/attendance',
      params: { eventId: event.id.toString() },
      search: { tab: 'going' },
    });
  }, [event.id, navigate]);

  const handleNavigateToAttendanceTab = useCallback(() => {
    navigate({
      to: '/events/$eventId/attendance',
      params: { eventId: event.id.toString() },
      search: { tab: 'attendance' },
    });
  }, [event.id, navigate]);

  const handleSave = useCallback(() => {
    const attendeeIds = selectedUserIdsRef.current;
    setErrorMessage(null);
    syncAttendance.mutate(
      {
        eventId: event.id,
        attendeeIds,
      },
      {
        onSuccess: () => handleNavigateToAttendanceTab(),
        onError: (mutationError) => {
          setErrorMessage(mutationError.message);
        },
      }
    );
  }, [event.id, handleNavigateToAttendanceTab, syncAttendance]);

  useActionBar(
    useMemo(
      () => ({
        visible: true,
        actions: [
          {
            key: 'cancel',
            label: AddAttendanceContent.buttons.cancel,
            variant: 'secondary',
            fullWidth: true,
            onClick: handleNavigateBack,
            disabled: syncAttendance.isPending,
          },
          {
            key: 'save',
            label: AddAttendanceContent.buttons.save,
            variant: 'default',
            fullWidth: true,
            loading: syncAttendance.isPending,
            disabled: !hasChanges || syncAttendance.isPending,
            onClick: handleSave,
          },
        ],
      }),
      [handleNavigateBack, handleSave, hasChanges, syncAttendance.isPending]
    )
  );

  const showEmptyPrompt =
    !searchTerm &&
    selectedUserIds.length === 0 &&
    suggestedProfiles.length === 0;
  const showNoMatches = searchTerm && visibleProfiles.length === 0;
  const showSuggested = !searchTerm && suggestedProfiles.length > 0;
  const showSelected = !searchTerm && selectedProfiles.length > 0;

  return (
    <div className="w-full max-w-4xl mx-auto bg-slate-700 pb-32 flex flex-col">
      <div className="flex-1 px-4 pt-6 space-y-2 flex flex-col">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={AddAttendanceContent.searchPlaceholder}
        />

        <section>
          {showEmptyPrompt && (
            <div className="rounded-md bg-slate-600 px-4 py-5 text-center text-body text-slate-300">
              {AddAttendanceContent.emptyPrompt}
            </div>
          )}

          {showNoMatches && !showEmptyPrompt && (
            <div className="rounded-md bg-slate-600 px-4 py-5 text-center text-body text-slate-300">
              {AddAttendanceContent.noMatches}
            </div>
          )}

          {/* When not searching: Show Suggested and Attendees sections */}
          {!searchTerm && (
            <>
              {showSuggested && (
                <div className="space-y-2">
                  <h2 className="text-body text-slate-300 px-2">
                    {AddAttendanceContent.suggestedLabel}
                  </h2>
                  <div className="rounded-md bg-slate-600">
                    <ul>
                      {suggestedProfiles.map((profile) => {
                        const userId = profile.id;
                        const isSelected = selectedUserIds.includes(userId);
                        const name =
                          profile.full_name ||
                          profile.email ||
                          AddAttendanceContent.memberWithoutProfile;
                        const initials = getInitials(name);

                        return (
                          <li key={userId}>
                            <UserListRow
                              name={name}
                              initials={initials}
                              isSelected={isSelected}
                              onClick={() => handleToggleUser(userId)}
                              className="px-8"
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              )}

              {showSelected && (
                <div className="space-y-2 mt-4">
                  <h2 className="text-body text-slate-300 px-2">
                    {AddAttendanceContent.attendeesLabel}
                  </h2>
                  <div className="rounded-md bg-slate-600">
                    <ul>
                      {selectedProfiles.map((profile) => {
                        const userId = profile.id;
                        const isSelected = selectedUserIds.includes(userId);
                        const name =
                          profile.full_name ||
                          profile.email ||
                          AddAttendanceContent.memberWithoutProfile;
                        const initials = getInitials(name);

                        return (
                          <li key={userId}>
                            <UserListRow
                              name={name}
                              initials={initials}
                              isSelected={isSelected}
                              onClick={() => handleToggleUser(userId)}
                              className="px-8"
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {/* When searching: Show search results */}
          {searchTerm && visibleProfiles.length > 0 && (
            <div className="rounded-md bg-slate-600">
              <ul>
                {visibleProfiles.map((profile) => {
                  const userId = profile.id;
                  const isSelected = selectedUserIds.includes(userId);
                  const name =
                    profile.full_name ||
                    profile.email ||
                    AddAttendanceContent.memberWithoutProfile;
                  const initials = getInitials(name);

                  return (
                    <li key={userId}>
                      <UserListRow
                        name={name}
                        initials={initials}
                        isSelected={isSelected}
                        onClick={() => handleToggleUser(userId)}
                        className="px-8"
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </section>

        {errorMessage && (
          <div className="text-red-300 text-small">{errorMessage}</div>
        )}
      </div>
    </div>
  );
}
